import React, { useState, useEffect } from 'react'
import { useHistory, Link as InLink } from 'react-router-dom'
import moment from 'moment'
import classnames from 'classnames'
import Grid from '@material-ui/core/Grid'
import TableContainer from '@material-ui/core/TableContainer'
import Table from '@material-ui/core/Table'
import TableRow from '@material-ui/core/TableRow'
import TableHead from '@material-ui/core/TableHead'
import TableBody from '@material-ui/core/TableBody'
import TableCell from '@material-ui/core/TableCell'
import Typography from '@material-ui/core/Typography'
import IconButton from '@material-ui/core/IconButton'
import Chip from '@material-ui/core/Chip'
import Link from '@material-ui/core/Link'
import Hidden from '@material-ui/core/Hidden'
import List from '@material-ui/core/List'
import ListItem from '@material-ui/core/ListItem'
import ListItemIcon from '@material-ui/core/ListItemIcon'
import ListItemText from '@material-ui/core/ListItemText'
import useMediaQuery from '@material-ui/core/useMediaQuery'
import useTheme from '@material-ui/core/styles/useTheme'
import Drawer from '@material-ui/core/Drawer'
import Skeleton from '@material-ui/lab/Skeleton'
import ArrowRightAlt from '@material-ui/icons/ArrowRightAlt'
import Cached from '@material-ui/icons/Cached'
import OpenInNew from '@material-ui/icons/ZoomOutMap'
import CloseIcon from '@material-ui/icons/Close'

// contexts
import { AppStateConsumer } from '../../contexts/AppStateContext'
import { useClient } from '../../contexts/ClientContext'

// styles
import useStyles from './styles'

// components
import PageTitle from '../../components/PageTitle/PageTitle'
import Widget from '../../components/Widget/Widget'
import { Tooltip } from '../../components/Wrappers'
import LogDetails from '../logDetails'

// utilities
import { getStatusTextSafe } from '../../utils'

export default function Debugger () {
  const classes = useStyles()
  const theme = useTheme()
  const { client, logs, fetchLogs } = useClient()
  const canShowDrawer = useMediaQuery(`(min-width:${theme.breakpoints.values.md}px)`)

  const history = useHistory()
  const [lastSelectedId, setLastSelectedId] = useState()
  const [pageError, setPageError] = useState(false)
  const [pageProcessing, setPageProcessing] = useState(false)
  const [showDrawer, setShowDrawer] = useState(false)

  useEffect(() => {
    let isCancelled = false

    async function runEffect () {
      try {
        if (client && !logs) {
          setPageProcessing(true)
          await fetchLogs(isCancelled)
        }
      } catch (error) {
        if (error.message !== 'Unauthorized') setPageError(true)
        console.log(error)
      }
      setPageProcessing(false)
    }

    function cancelEffect () {
      isCancelled = true
    }

    runEffect()
    return cancelEffect
  }, [client])

  const handleRowClick = (id) => () => {
    setLastSelectedId(id)
    if (canShowDrawer) setShowDrawer(true)
    else history.push(`/app/logs/${id}`)
  }

  const openFullLogView = () => history.push(`/app/logs/${lastSelectedId}`)

  return (
    <>
    <Drawer
      PaperProps={{ className: classes.drawer }}
      anchor='right'
      open={showDrawer}
      onClose={() => setShowDrawer(false)}
    >
      <Grid className={classes.drawerToolbar} container justify='flex-end'>
        <Grid item>
          <Tooltip
            title='Page view'
            arrow
            enterDelay={500}
            enterNextDelay={500}
          >
            <IconButton onClick={openFullLogView}>
              <OpenInNew />
            </IconButton>
          </Tooltip>
          <Tooltip
            title='Close'
            arrow
            enterDelay={500}
            enterNextDelay={500}
          >
            <IconButton onClick={() => setShowDrawer(false)}>
              <CloseIcon />
            </IconButton>
          </Tooltip>
        </Grid>
      </Grid>
      <LogDetails requestId={lastSelectedId} />
    </Drawer>
    <Grid container spacing={4}>
      <Hidden lgDown><Grid item xl={1}></Grid></Hidden>
      <Grid item xs={12} xl={10}>
        {pageError && <PageTitle title='Something went wrong!' />}

        {pageError &&
        <Typography variant='body1'>
          We were unable to fetch the log history for your app. Please reload the page to retry.
        </Typography>
        }

        {!pageError &&
        <AppStateConsumer>
          {({ appInitialized }) => appInitialized &&
          <>
          <PageTitle
            title="Debugger"
            button={{
              title: 'Refresh',
              disabled: pageProcessing,
              action: async () => {
                setPageProcessing(true)
                await fetchLogs()
                setPageProcessing(false)
              },
              icon: Cached
            }}
          />
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Widget disableWidgetMenu>
                {(!!pageProcessing || (!!logs && !!logs.length)) && <TableContainer>
                  <Table size='small'>
                    <TableHead>
                      <TableRow>
                        <TableCell width={300} align='left'>Status</TableCell>
                        <TableCell align='left'>Description</TableCell>
                        <TableCell align='right'>Request Time</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {!!pageProcessing && '......'.split('').map((e, i) =>
                        <TableRow key={i}>
                          <TableCell width={300}><Skeleton variant='rect' width='50%' /></TableCell>
                          <TableCell><Skeleton variant='rect'/></TableCell>
                          <TableCell>
                            <Skeleton
                              variant='rect'
                              width='50%'
                              style={{ float: 'right' }}
                            />
                          </TableCell>
                        </TableRow>
                      )}
                      {!pageProcessing && !!logs && logs.map(({ id, request, response }) => {
                        const status = `${response.status} ${getStatusTextSafe(response.status)}`
                        const requestTime = moment(request.time).format('M/D/YYYY, h:mm:ss a')
                        const isErrorStatus = response.status >= 400
                        return (
                          <TableRow
                            hover
                            key={id}
                            style={{ cursor: 'pointer' }}
                            onClick={handleRowClick(id)}
                          >
                            <TableCell width={300} align='left'>
                              <Chip
                                className={classnames(classes.statusChip, {
                                  [classes.chipError]: isErrorStatus,
                                  [classes.chipSuccess]: !isErrorStatus
                                })}
                                size='small'
                                label={status}
                              />
                            </TableCell>
                            <TableCell align='left'>
                              <Typography
                                display='inline'
                                variant='body2'
                                color='textSecondary'
                              >{request.verb} </Typography>
                              <Typography display='inline' variant='body2'>{request.route}</Typography>
                            </TableCell>
                            <TableCell align='right'>
                              <Typography variant='body2'>{requestTime}</Typography>
                            </TableCell>
                          </TableRow>
                        )
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>}
                {!pageProcessing && !!logs && !logs.length &&
                  <>
                    <Typography variant='h3' paragraph>
                      <Typography variant='inherit' color='textSecondary'>{client.display_name} </Typography>
                      is ready to debug!
                    </Typography>
                    <Typography variant='h6'>Next Steps</Typography>
                    <List>
                      <ListItem>
                        <ListItemIcon><ArrowRightAlt /></ListItemIcon>
                        <ListItemText disableTypography>
                          <Typography variant='body1'>
                            Check out the <Link href='https://docs.noonlight.com/reference' target='_blank'>
                              Documentation
                            </Link> to learn more about and try out our API products.
                          </Typography>
                        </ListItemText>
                      </ListItem>
                      <ListItem>
                        <ListItemIcon><ArrowRightAlt /></ListItemIcon>
                        <ListItemText disableTypography>
                          <Typography>
                            Go ahead and try making API requests using the Access Token on your <Link
                              component={InLink}
                              to='/app/dashboard'>
                              Dashboard
                            </Link>.
                          </Typography>
                        </ListItemText>
                      </ListItem>
                      <ListItem>
                        <ListItemIcon><ArrowRightAlt /></ListItemIcon>
                        <ListItemText>
                          Once you've made some API requests, you can return here to view their details.
                        </ListItemText>
                      </ListItem>
                    </List>
                  </>
                }
              </Widget>
            </Grid>
          </Grid>
          </>
          }
        </AppStateConsumer>}
      </Grid>
    </Grid>
    </>
  )
}
